/**
 * Enquire Modal
 * --------------------------------------------------
 * Modern modal handling using data attributes for state management
 *
 * Usage:
 * - Add data-enquire-action="open" to any element to open the modal
 * - Add data-enquire-action="close" to any element to close the modal
 * - Add data-enquire-action="download" to download links that require form completion
 * - Modal state is controlled via data-open="true/false" attribute
 * - Form visibility controlled via data-visible="true/false" attributes
 */

import Cookies from 'js-cookie';
import { PageUtils } from "./helpers/page-utils";

window.addEventListener('load', () => {
    const enquireModal = document.getElementById('enquire-modal');
    const enquireModalBackdrop = document.getElementById('enquire-modal-backdrop');
    const enquireDefaultForm = document.getElementById('enquire-default-form');
    const enquireDownloadForm = document.getElementById('enquire-download-form');
    const enquireDownloadsAvailable = document.getElementById('enquire-downloads-available');

    if (!enquireModal && !enquireModalBackdrop) return;

    const currentPathString = 'enquiry-download-form-' + window.location.pathname.replace(/\\|\//g,'');

    // Initialize enquire modal triggers
    function initEnquireModalTriggers() {
        // Add class to all enquire-now links
        const enquireNowLinks = document.querySelectorAll('a[href*="#enquire-now"]');
        enquireNowLinks.forEach(link => {
            link.classList.add('enquire-modal-open');
        });
    }

    // Get modal form elements
    const modalForm = enquireDefaultForm?.querySelector('form');
    const modalCampaignMonitorInput = modalForm?.querySelector('input[name="campaignMonitorEmailId"]');

    // Read the intended default from data attribute (Freeform can't override this)
    const modalOriginalCampaignMonitorEmailId = modalForm?.dataset.defaultCampaignMonitorEmailId || '';

    // Update the campaignMonitorEmailId hidden input in the modal form
    function updateCampaignMonitorEmailId(value) {
        if (modalCampaignMonitorInput) {
            // Use provided value, or fall back to modal's original value if empty/null
            modalCampaignMonitorInput.value = value || modalOriginalCampaignMonitorEmailId;
        }
    }

    // Reset the campaignMonitorEmailId hidden input to its original value
    function resetCampaignMonitorEmailId() {
        if (modalCampaignMonitorInput) {
            modalCampaignMonitorInput.value = modalOriginalCampaignMonitorEmailId;
        }
    }

    // Open modal functions
    function openEnquireModal(showDownloadForm = false, downloadUrl = null, downloadTitle = null) {
        enquireModal.dataset.open = 'true';
        enquireModalBackdrop.dataset.open = 'true';
        PageUtils.bodyLockToggleOn();

        if (showDownloadForm && enquireDownloadForm) {
            enquireDownloadForm.dataset.visible = 'true';
            if (enquireDefaultForm) enquireDefaultForm.dataset.visible = 'false';
            if (enquireDownloadsAvailable) enquireDownloadsAvailable.dataset.visible = 'false';

            // Update heading and store download info if provided
            if (downloadUrl && downloadTitle) {
                const heading = enquireDownloadForm.querySelector('[data-download-heading]');
                const subheading = enquireDownloadForm.querySelector('[data-download-subheading]');

                if (heading) {
                    heading.dataset.originalText = heading.textContent;
                    heading.textContent = 'Download Brochure';
                }

                if (subheading) {
                    subheading.dataset.originalText = subheading.textContent;
                    subheading.textContent = 'Please fill out the form below and your download will begin!';
                }

                // Store download URL on the form for later use
                const form = enquireDownloadForm.querySelector('form');
                if (form) {
                    form.dataset.pendingDownloadUrl = downloadUrl;
                    form.dataset.pendingDownloadTitle = downloadTitle;
                }
            }
        } else {
            if (enquireDefaultForm) {
                enquireDefaultForm.dataset.visible = 'true';

                // Update default form heading if download info provided
                if (downloadUrl) {
                    const heading = enquireDefaultForm.querySelector('[data-download-heading]');
                    const subheading = enquireDefaultForm.querySelector('[data-download-subheading]');

                    if (heading) {
                        heading.dataset.originalText = heading.textContent;
                        heading.textContent = 'Download Brochure';
                    }

                    if (subheading) {
                        subheading.dataset.originalText = subheading.textContent;
                        subheading.textContent = 'Please fill out the form below and your download will begin!';
                    }

                    // Store download URL on the form for later use
                    const form = enquireDefaultForm.querySelector('form');
                    if (form) {
                        form.dataset.pendingDownloadUrl = downloadUrl;
                        form.dataset.pendingDownloadTitle = downloadTitle || 'brochure';

                        // Set downloadedBrochure field to "Yes"
                        const downloadedBrochureField = form.querySelector('[name="downloadedBrochure"]');
                        if (downloadedBrochureField) {
                            downloadedBrochureField.value = 'Yes';
                        }
                    }
                }
            }
            if (enquireDownloadForm) enquireDownloadForm.dataset.visible = 'false';
            if (enquireDownloadsAvailable) enquireDownloadsAvailable.dataset.visible = 'false';
        }
    }

    function closeEnquireModal() {
        enquireModal.dataset.open = 'false';
        enquireModalBackdrop.dataset.open = 'false';
        PageUtils.bodyLockToggleOff();

        // Reset to default form after animation
        setTimeout(() => {
            // Re-query elements in case they weren't available at page load
            const defaultForm = document.getElementById('enquire-default-form');
            const downloadForm = document.getElementById('enquire-download-form');
            const downloadsAvailable = document.getElementById('enquire-downloads-available');

            if (defaultForm) defaultForm.dataset.visible = 'true';
            if (downloadForm) downloadForm.dataset.visible = 'false';
            if (downloadsAvailable) downloadsAvailable.dataset.visible = 'false';

            // Reset default form headings to original text
            if (defaultForm) {
                const heading = defaultForm.querySelector('[data-download-heading]');
                const subheading = defaultForm.querySelector('[data-download-subheading]');

                if (heading && heading.dataset.originalText) {
                    heading.textContent = heading.dataset.originalText;
                    delete heading.dataset.originalText;
                }

                if (subheading && subheading.dataset.originalText) {
                    subheading.textContent = subheading.dataset.originalText;
                    delete subheading.dataset.originalText;
                }

                // Clear pending download data
                const form = defaultForm.querySelector('form');
                if (form) {
                    delete form.dataset.pendingDownloadUrl;
                    delete form.dataset.pendingDownloadTitle;

                    // Clear downloadedBrochure field
                    const downloadedBrochureField = form.querySelector('[name="downloadedBrochure"]');
                    if (downloadedBrochureField) {
                        downloadedBrochureField.value = '';
                    }

                    // Reset campaignMonitorEmailId to original value
                    resetCampaignMonitorEmailId();
                }
            }

            // Reset download form headings to original text
            if (downloadForm) {
                const heading = downloadForm.querySelector('[data-download-heading]');
                const subheading = downloadForm.querySelector('[data-download-subheading]');

                if (heading && heading.dataset.originalText) {
                    heading.textContent = heading.dataset.originalText;
                    delete heading.dataset.originalText;
                }

                if (subheading && subheading.dataset.originalText) {
                    subheading.textContent = subheading.dataset.originalText;
                    delete subheading.dataset.originalText;
                }

                // Clear pending download data
                const form = downloadForm.querySelector('form');
                if (form) {
                    delete form.dataset.pendingDownloadUrl;
                    delete form.dataset.pendingDownloadTitle;
                }
            }
        }, 300);
    }

    // Event delegation for modal triggers
    document.addEventListener('click', (event) => {
        const target = event.target.closest('[data-enquire-action]');
        if (!target) return;

        const action = target.dataset.enquireAction;

        switch (action) {
            case 'open':
                event.preventDefault();
                openEnquireModal();
                // Update Campaign Monitor Email ID if provided on the trigger element
                const campaignMonitorEmailId = target.dataset.campaignMonitorEmailId;
                updateCampaignMonitorEmailId(campaignMonitorEmailId);
                break;

            case 'close':
                event.preventDefault();
                closeEnquireModal();
                break;

            case 'download':
                const hasCompletedForm = Cookies.get(currentPathString);

                if (!hasCompletedForm) {
                    // Prevent download and show form
                    event.preventDefault();

                    // Get download URL and title with smart fallback
                    const downloadUrl = target.href;
                    let downloadTitle = target.dataset.downloadTitle;

                    // Fallback: Try to find title in card structure
                    if (!downloadTitle) {
                        // Look for .text-2xl (common title class in cards)
                        const titleElement = target.querySelector('.text-2xl');
                        if (titleElement) {
                            downloadTitle = titleElement.textContent.trim();
                        } else {
                            // Last resort: use filename from URL
                            const urlParts = downloadUrl.split('/');
                            const filename = urlParts[urlParts.length - 1];
                            downloadTitle = filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
                        }
                    }

                    // Show download form with custom heading
                    openEnquireModal(false, downloadUrl, downloadTitle);

                    // Update Campaign Monitor Email ID if provided on the download element
                    const downloadCampaignMonitorEmailId = target.dataset.campaignMonitorEmailId;
                    updateCampaignMonitorEmailId(downloadCampaignMonitorEmailId);

                    // Set lot number if available
                    if (target.hasAttribute('data-lot')) {
                        const lotId = target.getAttribute('data-lot');
                        const lotNumberInputs = document.querySelectorAll('input[name="lotNumber"]');
                        lotNumberInputs.forEach(input => {
                            input.value = lotId;
                        });
                    }
                } else {
                    // Allow download to proceed
                    // Don't prevent default - let the browser handle the download
                }
                break;
        }
    });

    // Close modal when clicking on backdrop
    if (enquireModalBackdrop) {
        enquireModalBackdrop.addEventListener('click', (event) => {
            // Only close if clicking directly on the backdrop, not on modal content
            if (event.target === enquireModalBackdrop) {
                closeEnquireModal();
            }
        });
    }

    // Initialize on load
    initEnquireModalTriggers();
});